<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import type { GaiaJsonValue } from '@/api';
import type { ExtractedField } from '@/types/evaluation';
import GaiaButton from '../base/GaiaButton.vue';
import GaiaCodeBox from '../base/GaiaCodeBox.vue';
import GaiaFieldRow from '../base/GaiaFieldRow.vue';
import GaiaLink from '../base/GaiaLink.vue';
import GaiaRiskPill from '../base/GaiaRiskPill.vue';
import GaiaText from '../base/GaiaText.vue';
import GaiaExtractionCard from './GaiaExtractionCard.vue';
import GaiaExtractionFieldRow, {
  type GaiaExtractionEditValues,
  type GaiaExtractionFieldKey,
} from '../domain/extraction/GaiaExtractionFieldRow.vue';
import GaiaOverwriteBadge from '../domain/extraction/GaiaOverwriteBadge.vue';
import { reviewEvaluationRiskMap, reviewRiskLevel } from '../statusMaps';
import { gaiaTextButtonOverrides } from '../themes/tokens';

/**
 * One extracted field's body: what the document said, and what the playbook made
 * of it.
 *
 * Two cards rather than two tabs. The fields list groups by risk, so hiding the
 * evaluation behind a tab would hide the verdict that put the field in "High
 * risk" exactly where the user just clicked to find out why — and the cards
 * already own `#actions` and `#footer`, which a tab strip has nowhere to put.
 * A third card appears only when the run produced a redline suggestion.
 *
 * Presentation and local edit state only: it emits the collected edit values and
 * lets the view own `createOverwrite` / `updateOverwrite`, so a caller with
 * different endpoints (contract management's extraction modal) can adopt it.
 */

const props = defineProps({
  field: {
    type: Object as PropType<ExtractedField>,
    required: true,
  },
  canEdit: {
    type: Boolean,
    default: false,
  },
  /** Reveals the parts only an org admin is meant to read. */
  isAdmin: {
    type: Boolean,
    default: false,
  },
  /** Langfuse trace link; platform admins only, hidden when empty. */
  traceUrl: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits<{
  /** Keyed by field part — what the view hands to `buildOverwritePayload`. */
  submit: [values: Map<string, GaiaExtractionEditValues>];
  approve: [next: boolean];
  'page-click': [page: number, highlights: string[]];
}>();

const editMode = ref<boolean>(false);
const showPrompt = ref<boolean>(false);
const editValues = ref<Map<string, GaiaExtractionEditValues>>(new Map());

// A different field in the same slot must not inherit the last one's edits.
watch(
  () => props.field.field,
  () => {
    editMode.value = false;
    editValues.value = new Map();
  }
);

/** The parts the extraction card shows, in reading order. */
const EXTRACTION_KEYS: GaiaExtractionFieldKey[] = ['ai_value', 'ai_reason', 'ai_page'];

/** Shown to admins only — the quotes the answer was drawn from. */
const ADMIN_KEYS: GaiaExtractionFieldKey[] = ['ai_highlighted_text'];

const EVALUATION_KEYS: GaiaExtractionFieldKey[] = ['evaluation', 'evaluation_reason'];

/**
 * The label column. `GaiaFieldRow` sets its labels in the uppercase `label`
 * variant with letter-spacing, so "AI RESPONSE" is a good deal wider than the
 * mock's "Response" and wrapped at the 86px the showcase uses.
 */
const LABEL_WIDTH = 132;

const LABELS: Record<string, { own: string; ai: string } | undefined> = {
  ai_value: { own: 'Response', ai: 'AI response' },
  ai_reason: { own: 'Reason', ai: 'AI reason' },
  ai_page: { own: 'Page', ai: 'AI page' },
  ai_highlighted_text: { own: 'Highlights', ai: 'AI highlights' },
  evaluation: { own: 'Answer', ai: 'AI answer' },
  evaluation_reason: { own: 'Reason', ai: 'AI reason' },
};

const OVERWRITTEN_BY_KEY: Record<string, keyof ExtractedField | undefined> = {
  ai_value: 'has_value_overwrite',
  ai_reason: 'has_reason_overwrite',
  ai_page: 'has_page_overwrite',
  ai_highlighted_text: 'has_highlighted_text_overwrite',
  evaluation: 'has_evaluation_overwrite',
  evaluation_reason: 'has_evaluation_reason_overwrite',
};

/** A part a human has corrected is labelled as the answer, not as the AI's. */
function isOverwritten(key: string): boolean {
  const flag = OVERWRITTEN_BY_KEY[key];
  return flag ? Boolean(props.field[flag]) : false;
}

function labelFor(key: string): string {
  const label = LABELS[key];
  if (!label) return key;
  return isOverwritten(key) ? label.own : label.ai;
}

/** The value each row reads, overwrite first. */
function valueFor(key: string): GaiaJsonValue {
  switch (key) {
    case 'ai_value':
      return isOverwritten(key) ? props.field.overwritten_value : props.field.ai_value;
    case 'ai_reason':
      return (isOverwritten(key) ? props.field.overwritten_reason : props.field.ai_reason) ?? null;
    case 'ai_page':
      return (isOverwritten(key) ? props.field.overwritten_page : props.field.ai_page) ?? null;
    case 'ai_highlighted_text':
      return (
        (isOverwritten(key)
          ? props.field.overwritten_highlighted_text
          : props.field.ai_highlighted_text) ?? null
      );
    case 'evaluation':
      return (
        (isOverwritten(key)
          ? props.field.overwritten_evaluation
          : props.field.ai_evaluation?.answer) ?? null
      );
    case 'evaluation_reason':
      return (
        (isOverwritten(key)
          ? props.field.overwritten_evaluation_reason
          : props.field.ai_evaluation?.reason) ?? null
      );
    default:
      return null;
  }
}

const extractionKeys = computed(() =>
  props.isAdmin ? [...EXTRACTION_KEYS, ...ADMIN_KEYS] : EXTRACTION_KEYS
);

/** The page a "p. N" button jumps to, and the quotes it highlights there. */
const pageTarget = computed<number | null>(() => {
  const page = props.field.has_page_overwrite ? props.field.overwritten_page : props.field.ai_page;
  return typeof page === 'number' ? page : null;
});

const highlights = computed<string[]>(() => {
  const text = props.field.has_highlighted_text_overwrite
    ? props.field.overwritten_highlighted_text
    : props.field.ai_highlighted_text;
  return text ?? [];
});

const answer = computed<string | undefined>(() => {
  const value = valueFor('evaluation');
  if (Array.isArray(value)) return value[0];
  return typeof value === 'string' ? value : undefined;
});

const riskLevel = computed(() => reviewRiskLevel(answer.value));
const riskLabel = computed(() =>
  answer.value ? reviewEvaluationRiskMap[answer.value]?.label : undefined
);

const hasEvaluation = computed(
  () => valueFor('evaluation') !== null || valueFor('evaluation_reason') !== null
);

function collect(key: string, values: GaiaExtractionEditValues): void {
  editValues.value.set(key, values);
}

function submit(): void {
  emit('submit', editValues.value);
  editMode.value = false;
  editValues.value = new Map();
}

function cancel(): void {
  editMode.value = false;
  editValues.value = new Map();
}
</script>

<template>
  <n-flex vertical :size="14" :wrap="false">
    <gaia-extraction-card title="Extraction" subtitle="Information from document">
      <template #actions>
        <gaia-overwrite-badge v-if="isOverwritten('ai_value')" />
        <gaia-link
          v-if="props.traceUrl"
          :href="props.traceUrl"
          target="_blank"
          rel="noopener noreferrer"
          tone="muted"
        >
          Trace
        </gaia-link>
        <gaia-button
          v-if="props.canEdit && !editMode"
          text
          size="small"
          :theme-overrides="gaiaTextButtonOverrides.ink"
          @click="editMode = true"
        >
          Edit
        </gaia-button>
        <gaia-button size="tiny" @click="emit('approve', !props.field.approved)">
          {{ props.field.approved ? 'Unconfirm' : 'Confirm' }}
        </gaia-button>
      </template>

      <gaia-field-row
        v-for="key in extractionKeys"
        :key="key"
        :label="labelFor(key)"
        :label-width="LABEL_WIDTH"
        :divider="false"
      >
        <gaia-extraction-field-row
          :key-name="key"
          :value="valueFor(key)"
          :field-meta="props.field"
          :edit-mode="editMode"
          @update:edit-values="values => collect(key, values)"
        />
      </gaia-field-row>

      <gaia-field-row
        v-if="pageTarget !== null"
        label="Jump to"
        :label-width="LABEL_WIDTH"
        :divider="false"
      >
        <gaia-button size="tiny" @click="emit('page-click', pageTarget, highlights)">
          p. {{ pageTarget }}
        </gaia-button>
      </gaia-field-row>

      <template #footer>
        <n-flex align="center" :size="8" :wrap="false">
          <template v-if="editMode">
            <gaia-button size="small" type="primary" @click="submit">Save</gaia-button>
            <gaia-button size="small" @click="cancel">Cancel</gaia-button>
          </template>
          <gaia-button
            v-else-if="props.field.extraction_prompt"
            size="small"
            @click="showPrompt = !showPrompt"
          >
            {{ showPrompt ? 'Hide extraction prompt' : 'Show extraction prompt' }}
          </gaia-button>
        </n-flex>
      </template>
    </gaia-extraction-card>

    <gaia-code-box v-if="showPrompt && props.field.extraction_prompt" label="Extraction prompt">
      {{ props.field.extraction_prompt }}
    </gaia-code-box>

    <gaia-extraction-card v-if="hasEvaluation" title="Evaluation">
      <template #actions>
        <gaia-risk-pill v-if="riskLabel" :level="riskLevel" pill>{{ riskLabel }}</gaia-risk-pill>
        <gaia-overwrite-badge v-if="isOverwritten('evaluation')" />
      </template>

      <gaia-field-row
        v-for="key in EVALUATION_KEYS"
        :key="key"
        :label="labelFor(key)"
        :label-width="LABEL_WIDTH"
        :divider="false"
      >
        <gaia-extraction-field-row
          :key-name="key"
          :value="valueFor(key)"
          :field-meta="props.field"
          :edit-mode="editMode"
          @update:edit-values="values => collect(key, values)"
        />
      </gaia-field-row>
    </gaia-extraction-card>

    <!-- The playbook's proposed redline, when the run drafted one. -->
    <slot name="suggestion" />

    <gaia-text v-if="!props.canEdit" size="meta" tone="subtle">
      You do not have permission to edit this review.
    </gaia-text>
  </n-flex>
</template>
